import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Loader2, XCircle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const money = (n, cur = "usd") =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${
    cur && cur.toLowerCase() !== "usd" ? ` ${cur.toUpperCase()}` : ""
  }`;
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

/** Emailed self-serve page for a monthly contribution. No login required — the link itself
 *  is the credential, and nothing personal is shown beyond a masked email. */
export default function DonationManage() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | active | cancelled | invalid
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/donations/manage/${token}`)
      .then(({ data }) => { setInfo(data); setState(data.active ? "active" : "cancelled"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/donations/manage/${token}/cancel`);
      setState("cancelled");
    } catch (e) {
      setError(e?.response?.data?.detail || "We couldn't cancel it just now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col justify-center px-4 py-16" data-testid="donation-manage-page">
      {state === "loading" && (
        <div className="text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
          <p className="mt-4 text-muted-foreground">Looking up your contribution…</p>
        </div>
      )}

      {state === "invalid" && (
        <Card className="p-8 text-center" data-testid="donation-manage-invalid">
          <XCircle className="mx-auto h-12 w-12 text-muted-foreground" />
          <h1 className="mt-5 text-2xl font-[720] text-foreground">This link isn&apos;t valid</h1>
          <p className="mt-3 text-muted-foreground">
            It may have already been used or the contribution has ended. Email{" "}
            <a href="mailto:support@startupotter.com" className="text-primary hover:underline">support@startupotter.com</a>{" "}
            and we&apos;ll help you or cancel it on your behalf.
          </p>
          <div className="mt-7"><Link to="/pricing"><Button variant="secondary">Back to pricing</Button></Link></div>
        </Card>
      )}

      {state === "active" && (
        <Card className="p-8" data-testid="donation-manage-active">
          <div className="flex items-center gap-2 text-primary">
            <Heart className="h-5 w-5" />
            <span className="text-xs font-[640] uppercase tracking-wide">Your monthly support</span>
          </div>
          <h1 className="mt-4 text-2xl font-[720] text-foreground">
            {money(info.amount, info.currency)} a month
          </h1>
          <dl className="mt-5 space-y-2.5 text-sm">
            <div className="flex justify-between border-b border-border/60 pb-2">
              <dt className="text-muted-foreground">Started</dt>
              <dd className="font-[540] text-foreground">{fmtDate(info.started_at) || "—"}</dd>
            </div>
            <div className="flex justify-between border-b border-border/60 pb-2">
              <dt className="text-muted-foreground">Receipts go to</dt>
              <dd className="font-[540] text-foreground" data-testid="donation-manage-email">{info.email_masked || "—"}</dd>
            </div>
          </dl>
          <p className="mt-5 text-sm text-muted-foreground">
            Thank you — this genuinely keeps Startup Otter running. Cancelling stops all future charges
            immediately; nothing already given is refunded.
          </p>
          {error && <p className="mt-4 text-sm text-destructive" data-testid="donation-manage-error">{error}</p>}
          <div className="mt-7 flex flex-wrap gap-3">
            <Button variant="destructive" onClick={cancel} disabled={busy} data-testid="donation-manage-cancel">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Stop my monthly contribution
            </Button>
            <Link to="/pricing"><Button variant="secondary" data-testid="donation-manage-keep">Keep supporting</Button></Link>
          </div>
          <p className="mt-5 text-xs text-muted-foreground">
            Trouble with this page? Email{" "}
            <a href="mailto:support@startupotter.com" className="text-primary hover:underline">support@startupotter.com</a>{" "}
            and we can cancel it for you.
          </p>
        </Card>
      )}

      {state === "cancelled" && (
        <Card className="p-8 text-center" data-testid="donation-manage-cancelled">
          <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
          {info?.status !== "paid" ? (
            <>
              <h1 className="mt-5 text-2xl font-[720] text-foreground">Nothing to cancel</h1>
              <p className="mt-3 text-muted-foreground">
                This contribution was never charged, so there&apos;s nothing to stop. If you think you were
                charged, email{" "}
                <a href="mailto:support@startupotter.com" className="text-primary hover:underline">support@startupotter.com</a>{" "}
                and we&apos;ll check for you.
              </p>
            </>
          ) : (
            <>
              <h1 className="mt-5 text-2xl font-[720] text-foreground">Your monthly support has ended</h1>
              <p className="mt-3 text-muted-foreground">
                You won&apos;t be charged again. Thank you for everything you gave while you were supporting us —
                we&apos;ve emailed you a confirmation.
              </p>
            </>
          )}
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/pricing"><Button data-testid="donation-manage-back">Back to pricing</Button></Link>
            <Link to="/contact"><Button variant="secondary">Contact support</Button></Link>
          </div>
        </Card>
      )}
    </div>
  );
}
